import GeoJSON from "ol/format/GeoJSON";
import { ILayerFactory } from "./ILayerFactory";
import { Feature, Map } from "ol";
import { SelectedFeatureInfo } from "../SelectedFeatureInfo";
import { MapContextMenuItem } from "../MapContextMenuItem";
import { ODQuantityLayerName } from "./ODQuantityLayerName";
import ImageLayer from "ol/layer/Image";
import { CqlParameter } from "../net/CqlParameter";
import { WMSGetFeatureManager } from "../net/WMSGetFeatureManager";
import { AttributeType } from "../define/AttributeType";
import { IAttributeConvert } from "@/attribute/convert/IAttributeConvert";
import { FeatureGeoJson } from "../FeatureGeoJson";
import { ODNodeAttributeConvert } from "@/attribute/convert/ODNodeAttributeConvert";
import { ODArrivalsNodeAttributeConvert } from "@/attribute/convert/ODArrivalsNodeAttributeConvert";
import { WFSGetFeatureManager } from "../net/WFSGetFeatureManager";
import { Layer } from "ol/layer";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { ODRangeNodeAttributeConvert } from "@/attribute/convert/ODRangeNodeAttributeConvert";
import { Circle, Fill, Stroke, Style } from "ol/style";
import { Geometry } from "ol/geom";
import { ODDataType } from "./IODLayerName";
import ImageWMSEx from "../net/ImageWMSEx";
import { IOdDisplayEvent } from "./IOdDisplayEvent";
import { asArray } from "ol/color";
import { LayerUtil } from "../util/LayerUtil";

/**
 * 最大ズームレベル
 */
const LAYER_MAX_ZOOMLEVEL = 18;

/**
 * 最小ズームレベル
 */
const LAYER_MIN_ZOOMLEVEL: Record<ODDataType, number> = {
  Bゾーン: 11,
  市区町村: 9,
  都道府県: 5,
};

/**
 * API通信用URL
 */
const URL_API = process.env.VUE_APP_XROAD_API_URL;

/**
 * Bゾーンレイヤ名
 */
const OD_BZONE_LAYER_NAME = [
  /** OD量発ゾーン情報_Bゾーン_乗用車レイヤ名 */
  "t_od_stzone_bzone_passenger_car",
  /** OD量発ゾーン情報_Bゾーン_小型貨物車レイヤ名 */
  "t_od_stzone_bzone_small_cargo",
  /** OD量発ゾーン情報_Bゾーン_普通貨物車レイヤ名 */
  "t_od_stzone_bzone_normal_cargo",
  /** OD量発ゾーン情報_Bゾーン_車種計レイヤ名 */
  "t_od_stzone_bzone_total",
  /** OD量着ゾーン情報_Bゾーン_乗用車レイヤ名 */
  "t_od_arzone_bzone_passenger_car",
  /** OD量着ゾーン情報_Bゾーン_小型貨物車レイヤ名 */
  "t_od_arzone_bzone_small_cargo",
  /** OD量着ゾーン情報_Bゾーン_普通貨物車レイヤ名 */
  "t_od_arzone_bzone_normal_cargo",
  /** OD量着ゾーン情報_Bゾーン_車種計レイヤ名 */
  "t_od_arzone_bzone_total",
];

/**
 * OD量発ゾーン都道府県レイヤ名
 */
const OD_PREF_LAYER_NAME = [
  /** OD量発ゾーン情報_都道府県_乗用車レイヤ名 */
  "t_od_stzone_pref_passenger_car",
  /** OD量発ゾーン情報_都道府県_小型貨物車レイヤ名 */
  "t_od_stzone_pref_small_cargo",
  /** OD量発ゾーン情報_都道府県_普通貨物車レイヤ名 */
  "t_od_stzone_pref_normal_cargo",
  /** OD量発ゾーン情報_都道府県_車種計レイヤ名 */
  "t_od_stzone_pref_normal_total",
];

/**
 * OD量左クリック選択時スタイルセット用キー定義
 */
const LEFT_SELECTED_STLE = "leftSelectedStyle";

/**
 * OD量発ノード選択半径配列
 */
const departureNodeRadiusArray: number[] = [];

/**
 * ポリゴンの色と円のサイズ返却用型定義
 */
interface pointSizeAndPolygonColor {
  pointSize: number;
  polygonColor: string;
}

/**
 * ポリゴンの色と円のサイズ返却メソッド
 */
const FUNCTION_getPointSizeAndPolygonColor = (
  source: number,
  isArrival?: boolean
): pointSizeAndPolygonColor => {
  //算出した%に応じてポリゴンの色とポイントサイズを設定
  let polygonColor = null;
  let pointSizePixel = null;
  if (source <= 10) {
    if (isArrival) {
      polygonColor = "#2C74B4";
    } else {
      polygonColor = "#FFFFFF";
    }
    pointSizePixel = 5;
  } else if (source >= 11 && source <= 20) {
    if (isArrival) {
      polygonColor = "#4169E1";
    } else {
      polygonColor = "#EEEEEE";
    }
    pointSizePixel = 10;
  } else if (source >= 21 && source <= 30) {
    if (isArrival) {
      polygonColor = "#87CEEB";
    } else {
      polygonColor = "#D4D9DC";
    }
    pointSizePixel = 15;
  } else if (source >= 31 && source <= 40) {
    if (isArrival) {
      polygonColor = "#00FF7F";
    } else {
      polygonColor = "#D3D3D3";
    }
    pointSizePixel = 20;
  } else if (source >= 41 && source <= 50) {
    if (isArrival) {
      polygonColor = "#FFFACD";
    } else {
      polygonColor = "#BCBACE";
    }
    pointSizePixel = 25;
  } else if (source >= 51 && source <= 60) {
    if (isArrival) {
      polygonColor = "#FCC404";
    } else {
      polygonColor = "#A9A9A9";
    }
    pointSizePixel = 30;
  } else if (source >= 61 && source <= 70) {
    if (isArrival) {
      polygonColor = "#FF9900";
    } else {
      polygonColor = "#7D7B83";
    }
    pointSizePixel = 35;
  } else if (source >= 71 && source <= 80) {
    if (isArrival) {
      polygonColor = "#FA8072";
    } else {
      polygonColor = "#666666";
    }
    pointSizePixel = 40;
  } else if (source >= 81 && source <= 90) {
    if (isArrival) {
      polygonColor = "#FF1818";
    } else {
      polygonColor = "#5D5050";
    }
    pointSizePixel = 45;
  } else if (source >= 91) {
    if (isArrival) {
      polygonColor = "#D40C0C";
    } else {
      polygonColor = "#323236";
    }
    pointSizePixel = 50;
  }

  return {
    pointSize: pointSizePixel,
    polygonColor: polygonColor,
  };
};

const FUNCTION_getSelectedFeatures = async (
  bBox: number[],
  cqlParameters: CqlParameter[],
  x: number,
  y: number,
  height: number,
  width: number
): Promise<Feature<Geometry>[]> => {
  const WMSGetter = new WMSGetFeatureManager();
  const selectedFeatureGeoJSON = await WMSGetter.getInfo(
    URL_API,
    bBox.join(","),
    cqlParameters,
    x,
    y,
    height,
    width
  ).catch((error: any) => {
    throw error;
  });
  return new GeoJSON().readFeatures(selectedFeatureGeoJSON);
};

/**
 * OD量レイヤ表示クラス
 */
export class ODNodeLayerFactory implements ILayerFactory {
  /**
   * OD発ノードレイヤ
   */
  private _departureLayer: ImageLayer<ImageWMSEx>;

  /**
   * OD発ノードWMSソース
   */
  private _departureWmsImageSource: ImageWMSEx;

  /**
   * OD着ノードレイヤ
   */
  private _arrivalLayer: ImageLayer<ImageWMSEx>;

  /**
   * OD着ノードWMSソース
   */
  private _arrivalWmsImageSource: ImageWMSEx;

  /**
   * 対象データ出力レイヤ
   */
  private _viewLayer: VectorLayer<VectorSource>;

  /**
   * 対象データソース
   */
  private _vectorSource: VectorSource;

  set viewLayer(layer: VectorLayer<VectorSource>) {
    this._viewLayer = layer;
  }

  /**
   * 表示対象OD量レイヤ
   */
  private _visibleODLayer: ODQuantityLayerName;

  /**
   * 選択中発ノード
   */
  private startNode: string;

  /**
   * OD量コンバータ
   */
  private selectedAttributeConverter: ODNodeAttributeConvert;

  /**
   * OD量データ表示開始・終了時イベント
   */
  private _odDisplayEvent: IOdDisplayEvent;

  /**
   * 一時選択中発ノード
   */
  private tmpStartNode: string;

  /**
   * 一時発ノード地物
   */
  private tmpStartFeature: Feature;

  /**
   * 一時OD量コンバータ
   */
  private tmpSelectedAttributeConverter: ODNodeAttributeConvert;

  /**
   * パラメータ更新フラグ
   */
  private isUpdateParams = false;

  /**
   * 地物属性の有無を取得
   */
  isFeatureInfo(): boolean {
    return true;
  }

  /**
   * 範囲地物属性の有無を取得
   */
  isRangeFeatureInfo(): boolean {
    return true;
  }

  /**
   * 右クリック処理の有無を取得
   */
  isRightClickInfo(): boolean {
    return true;
  }

  /**
   * レイヤ定義を取得
   * @param _map openLayers Mapクラス
   */
  getViewLayer(_map?: Map): Layer[] {
    const resultArray = [];

    //各レイヤの作成
    //OD発ノードWMSレイヤの作成
    this._departureWmsImageSource = new ImageWMSEx({
      url: URL_API,
      params: {
        LAYERS: null,
        EXCEPTIONS: "application/json",
        CQL_FILTER: null,
      },
      ratio: 1,
      serverType: "geoserver",
      crossOrigin: "Anonymous",
    });
    this._departureLayer = new ImageLayer({
      source: this._departureWmsImageSource,
      maxZoom: LAYER_MAX_ZOOMLEVEL,
      visible: false,
    });

    //OD着ノードWMSレイヤの作成
    this._arrivalWmsImageSource = new ImageWMSEx({
      url: URL_API,
      params: {
        EXCEPTIONS: "application/json",
      },
      ratio: 1,
      serverType: "geoserver",
      crossOrigin: "Anonymous",
    });
    this._arrivalWmsImageSource.on("imageloadend", () => {
      if (!this.isUpdateParams) return;
      // //着ノードレイヤの表示状態を表示にする
      this._arrivalLayer.setOpacity(1);
      this.startNode = this.tmpStartNode;
      this.selectedAttributeConverter = this.tmpSelectedAttributeConverter;
      this._odDisplayEvent.onOdNodeSelected(
        this.tmpStartNode,
        this.tmpStartFeature,
        this.tmpSelectedAttributeConverter
      );
      this.isUpdateParams = false;
    });
    this._arrivalLayer = new ImageLayer({
      source: this._arrivalWmsImageSource,
      maxZoom: LAYER_MAX_ZOOMLEVEL,
      visible: false,
    });

    //OD発選択Layer作成
    this._vectorSource = new VectorSource();
    this._viewLayer = new VectorLayer({
      source: this._vectorSource,
      maxZoom: LAYER_MAX_ZOOMLEVEL,
      visible: false,
    });

    resultArray.push(this._departureLayer, this._arrivalLayer, this._viewLayer);
    return resultArray;
  }

  /**
   * 地物情報を取得
   * @param map 地図
   * @param latitude クリック座標緯度
   * @param longitude クリック座標経度
   * @param x クリック位置X
   * @param y クリック位置Y
   * @param width 地図幅
   * @param height 地図高さ
   * @param bBox 地図範囲（BBOX)
   */
  async getFeatureInfo(
    map: Map,
    latitude: number,
    longitude: number,
    x: number,
    y: number,
    width: number,
    height: number,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    departureNodeRadiusArray.length = 0;
    if (
      OD_PREF_LAYER_NAME.includes(this._visibleODLayer.getLayerName()) &&
      (map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
        map.getView().getZoom() <=
          LAYER_MIN_ZOOMLEVEL[this._visibleODLayer.odDataType])
    ) {
      return null;
    } else if (
      !OD_PREF_LAYER_NAME.includes(this._visibleODLayer.getLayerName()) &&
      (map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
        map.getView().getZoom() <=
          LAYER_MIN_ZOOMLEVEL[this._visibleODLayer.odDataType])
    ) {
      return null;
    }

    let contextMenuName = "";
    if (this._visibleODLayer.getLayerName().includes("bzone")) {
      contextMenuName = "OD_OD交通量_Bゾーン";
    } else if (this._visibleODLayer.getLayerName().includes("city")) {
      contextMenuName = "OD_OD交通量_市区町村";
    } else if (this._visibleODLayer.getLayerName().includes("pref")) {
      contextMenuName = "OD_OD交通量_都道府県";
    }

    //選択地物情報リスト
    let selectedFeatureInfoList: SelectedFeatureInfo[] = [];

    //OD発選択レイヤから選択地点の地物を取得する
    const pixel = [x, y];
    const vectorFeatures = map.getFeaturesAtPixel(pixel, {
      layerFilter: (layer: Layer) => {
        if (this._viewLayer == layer) {
          return true;
        }
      },
    });
    if (vectorFeatures.length !== 0) {
      await vectorFeatures.forEach(async (featureLike) => {
        // FeatureLike型をFeatureにキャスト
        const feature = new Feature(featureLike.getProperties());
        // キャストでIDがセットされないため、別途IDをセット
        feature.setId(featureLike.getId());

        const geometry = feature.getGeometry().clone();
        const selectFeature = new Feature({
          geometry,
        });
        selectFeature.setId(feature.getId());
        selectFeature.setStyle(feature.get(LEFT_SELECTED_STLE));

        // GeoJSONを生成
        const geoJson = new GeoJSON().writeFeature(feature);
        // feature内Idを取得
        const fId = feature.getId().toString();
        const featureGeoJson = new FeatureGeoJson(geoJson, fId);

        // 作成したデータをもとにSelectedFeatureInfoを作成してreturn
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          contextMenuName,
          featureGeoJson,
          AttributeType.Normal,
          this.selectedAttributeConverter
        );
        selectedFeatureInfoList.push(selectedFeatureInfo);
      });
    }

    //OD発ノードレイヤが表示状態であれば地物の情報を取得
    if (this._departureLayer.getVisible()) {
      const cqlParameters = [
        new CqlParameter(
          this._visibleODLayer.getLayerName(),
          this._visibleODLayer.getCqlFilterString()
        ),
      ];
      //対象地点の発ノード情報を取得
      const departureSelectedFeatures = await FUNCTION_getSelectedFeatures(
        bBox,
        cqlParameters,
        x,
        y,
        height,
        width
      );
      if (departureSelectedFeatures.length !== 0) {
        await this.getRightClickInfo(
          map,
          latitude,
          longitude,
          x,
          y,
          width,
          height,
          bBox
        ).then(async (rightClickInfo) => {
          await Promise.all(
            departureSelectedFeatures.map(async (feature, index) => {
              const id = feature.getId().toString();
              const layerKey = id.split(".")[0];

              const WFSGetter = new WFSGetFeatureManager();
              const wfsGeoJson = await WFSGetter.getInfo(
                URL_API,
                cqlParameters,
                null,
                feature.getId()
              ).catch((error) => {
                throw error;
              });
              const wfsFeature = new GeoJSON()
                .readFeatures(wfsGeoJson)
                .find((feature) => feature.getId() === id);
              if (!wfsFeature) return;

              //属性変換クラス・attributeクラスを生成
              let attributeConvert: IAttributeConvert = null;
              let attributeType: AttributeType = null;

              // 属性変換(本番用)
              attributeConvert = new ODNodeAttributeConvert(
                wfsFeature,
                this._visibleODLayer.odDataType,
                this._visibleODLayer.carType
              );

              attributeType = AttributeType.Normal;

              // selectFeatureを生成（地物情報のみ）
              const geometry = wfsFeature.getGeometry();
              const selectFeature = new Feature({
                geometry,
              });
              selectFeature.setId(id);
              //対象地物のスタイルを設定
              const selectStyle = new Style({
                image: new Circle({
                  stroke: new Stroke({
                    width: 3,
                    color: "hotpink",
                  }),
                  radius: departureNodeRadiusArray[index],
                  fill: new Fill({
                    color: "rgba(255,0,0,0.6)",
                  }),
                }),
              });
              selectFeature.setStyle(selectStyle);
              // featureGeoJSONを生成
              const geoJSON = new GeoJSON().writeFeature(wfsFeature);
              const featureGeoJSON = new FeatureGeoJson(geoJSON, id);
              // 作成したデータをもとにSelectedFeatureInfoを作成
              const selectedFeatureInfo = new SelectedFeatureInfo(
                selectFeature,
                contextMenuName,
                featureGeoJSON,
                attributeType,
                attributeConvert,
                false,
                null,
                layerKey,
                null,
                null,
                null,
                rightClickInfo[index].contextClickCallBack
              );
              selectedFeatureInfoList.push(selectedFeatureInfo);
            })
          );
        });
      }
    }
    //OD着ノードレイヤが表示状態であれば地物の情報を取得
    if (this._arrivalLayer.getVisible()) {
      const cqlParameters = [
        new CqlParameter(
          this._visibleODLayer.getLayerName(this.startNode),
          this._visibleODLayer.getCqlFilterString(
            this.startNode,
            "getFeatureInfo"
          )
        ),
      ];
      // 対象地点の着ノード情報を取得
      const arrivalSelectedFeatures = await FUNCTION_getSelectedFeatures(
        bBox,
        cqlParameters,
        x,
        y,
        height,
        width
      );
      if (arrivalSelectedFeatures.length !== 0) {
        selectedFeatureInfoList = selectedFeatureInfoList.concat(
          await Promise.all(
            arrivalSelectedFeatures.map(async (feature) => {
              const id = feature.getId().toString();
              const layerKey = id.split(".")[0];

              const WFSGetter = new WFSGetFeatureManager();
              const wfsGeoJson = await WFSGetter.getInfo(
                URL_API,
                cqlParameters,
                null,
                feature.getId()
              ).catch((error) => {
                throw error;
              });
              const wfsFeature = new GeoJSON()
                .readFeatures(wfsGeoJson)
                .find((feature) => feature.getId() === id);
              if (!wfsFeature) return;

              //属性変換クラス・attributeクラスを生成
              let attributeConvert: IAttributeConvert = null;
              let attributeType: AttributeType = null;

              // 属性変換(本番用)
              attributeConvert = new ODArrivalsNodeAttributeConvert(
                wfsFeature,
                this._visibleODLayer.odDataType,
                this._visibleODLayer.carType
              );

              attributeType = AttributeType.Normal;

              // selectFeatureを生成（地物情報のみ）
              const geometry = wfsFeature.getGeometry();
              const selectFeature = new Feature({
                geometry,
              });
              selectFeature.setId(id);
              //円図形比率取得
              const circleRatio = feature.get("円図形比率");

              //スタイルとして設定するポリゴンの色やポイントサイズを取得
              const pointSizeAndPolygonColor =
                FUNCTION_getPointSizeAndPolygonColor(circleRatio * 100, true);

              //レイヤがBゾーンかそれ以外かで対象地物のスタイルを設定
              if (OD_BZONE_LAYER_NAME.includes(layerKey)) {
                const selectStyle = new Style({
                  stroke: new Stroke({ width: 3, color: "hotpink" }),
                  fill: new Fill({
                    color: pointSizeAndPolygonColor.polygonColor,
                  }),
                });
                selectFeature.setStyle(selectStyle);
              } else {
                const selectStyle = new Style({
                  image: new Circle({
                    stroke: new Stroke({ width: 3, color: "hotpink" }),
                    radius: pointSizeAndPolygonColor.pointSize,
                    fill: new Fill({
                      color: "rgba(0,102,204,0.6)",
                    }),
                  }),
                });
                selectFeature.setStyle(selectStyle);
              }

              // featureGeoJSONを生成
              const geoJSON = new GeoJSON().writeFeature(wfsFeature);
              const featureGeoJSON = new FeatureGeoJson(geoJSON, id);
              // selectedFeatureInfoを生成して、リストに追加
              const selectedFeatureInfo = new SelectedFeatureInfo(
                selectFeature,
                contextMenuName,
                featureGeoJSON,
                attributeType,
                attributeConvert as IAttributeConvert,
                false,
                null,
                layerKey
              );
              return selectedFeatureInfo;
            })
          )
        );
      }
    }

    return selectedFeatureInfoList;
  }

  /**
   * 指定範囲の地物を取得
   * @param _map 地図
   * @param _bBox 地図範囲（BBOX)
   */
  async getRangeFeatureInfo(
    _map: Map,
    _bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    if (
      OD_PREF_LAYER_NAME.includes(this._visibleODLayer.getLayerName()) &&
      (_map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
        _map.getView().getZoom() <=
          LAYER_MIN_ZOOMLEVEL[this._visibleODLayer.odDataType])
    ) {
      return null;
    } else if (
      !OD_PREF_LAYER_NAME.includes(this._visibleODLayer.getLayerName()) &&
      (_map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
        _map.getView().getZoom() <=
          LAYER_MIN_ZOOMLEVEL[this._visibleODLayer.odDataType])
    ) {
      return null;
    }

    let contextMenuName = "";
    if (this._visibleODLayer.getLayerName().includes("bzone")) {
      contextMenuName = "OD_OD交通量_Bゾーン";
    } else if (this._visibleODLayer.getLayerName().includes("city")) {
      contextMenuName = "OD_OD交通量_市区町村";
    } else if (this._visibleODLayer.getLayerName().includes("pref")) {
      contextMenuName = "OD_OD交通量_都道府県";
    }

    //選択地物情報リスト
    const featureInfoList: SelectedFeatureInfo[] = [];
    const WFSGetter = new WFSGetFeatureManager();

    //OD発ノードレイヤが表示状態であれば指定範囲の地物情報を取得し、リストに格納
    if (this._departureLayer.getVisible()) {
      const featureGeoJSON = await WFSGetter.getInfo(
        URL_API,
        [
          new CqlParameter(
            this._visibleODLayer.getLayerName(),
            this._visibleODLayer.getCqlFilterString()
          ),
        ],
        _bBox
      ).catch((error: any) => {
        throw error;
      });
      const features = new GeoJSON().readFeatures(featureGeoJSON);
      if (features.length === 0) return null;
      features.forEach((feature) => {
        const id = feature.getId().toString();

        //属性変換クラス・attributeクラスを生成
        let attributeConvert: IAttributeConvert = null;
        let attributeType: AttributeType = null;

        const geoJSON = new GeoJSON().writeFeature(feature);

        //属性変換クラスを設定
        attributeConvert = new ODRangeNodeAttributeConvert(
          feature,
          this._visibleODLayer.odDataType,
          this._visibleODLayer.carType
        );

        attributeType = AttributeType.Normal;

        // selectFeatureを生成（地物情報のみ）
        const geometry = feature.getGeometry().clone();
        const selectFeature = new Feature({
          geometry,
        });

        // featureGeoJSONを生成
        const featureGeoJSON = new FeatureGeoJson(geoJSON, id);
        // selectedFeatureInfoを生成して、リストに追加
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          contextMenuName,
          featureGeoJSON,
          attributeType,
          attributeConvert as IAttributeConvert
        );

        featureInfoList.push(selectedFeatureInfo);
      });
    }

    //OD発選択レイヤが表示状態であれば指定範囲の地物情報を取得し、リストに格納
    if (this._viewLayer.getVisible()) {
      //BBOX範囲内の地物を取得し、返却リストに格納
      this._vectorSource.forEachFeatureIntersectingExtent(_bBox, (feature) => {
        const id = feature.getId().toString();
        const geoJSON = new GeoJSON().writeFeature(feature);

        // featureGeoJSONを生成
        const featureGeoJSON = new FeatureGeoJson(geoJSON, id);

        // selectFeatureを生成（地物情報のみ）
        const geometry = feature.getGeometry().clone();
        const selectFeature = new Feature({
          geometry,
        });

        // selectedFeatureInfoを生成して、リストに追加
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          contextMenuName,
          featureGeoJSON,
          AttributeType.Normal,
          new ODRangeNodeAttributeConvert(
            this.selectedAttributeConverter.feature,
            this.selectedAttributeConverter.odDataType,
            this.selectedAttributeConverter.carType,
            true
          ) as IAttributeConvert
        );
        featureInfoList.push(selectedFeatureInfo);
      });
    }

    // ②OD着ノードLayerが表示状態か確認する。非表示であれば返却リストを返却する。
    if (!this._arrivalLayer.getVisible()) {
      return featureInfoList;
    }
    // ③OD着ノードLayerから引数のBBOXの範囲、およびCQLFitlerに発ノード=内部変数：選択中発ノードを指定し、呼び出し、内部変数:選択中発ノードを発ノードに持つOD着ノード情報を取得し、返却リストに格納する。
    const arrivalFeatureGeoJSON = await WFSGetter.getInfo(
      URL_API,
      [
        new CqlParameter(
          this._visibleODLayer.getLayerName(this.startNode),
          this._visibleODLayer.getCqlFilterString(
            this.startNode,
            "getRangeFeatureInfo"
          )
        ),
      ],
      _bBox
    ).catch((error: any) => {
      throw error;
    });
    const arrivalFeatures = new GeoJSON().readFeatures(arrivalFeatureGeoJSON);
    if (arrivalFeatures.length !== 0) {
      arrivalFeatures.forEach((feature) => {
        const id = feature.getId().toString();

        //属性変換クラス・attributeクラスを生成
        let attributeConvert: IAttributeConvert = null;
        let attributeType: AttributeType = null;

        const geoJSON = new GeoJSON().writeFeature(feature);

        attributeConvert = new ODRangeNodeAttributeConvert(
          feature,
          this._visibleODLayer.odDataType,
          this._visibleODLayer.carType,
          true
        );
        attributeType = AttributeType.Normal;

        // selectFeatureを生成（地物情報のみ）
        const geometry = feature.getGeometry().clone();
        const selectFeature = new Feature({
          geometry,
        });

        // featureGeoJSONを生成
        const featureGeoJSON = new FeatureGeoJson(geoJSON, id);
        // selectedFeatureInfoを生成して、リストに追加
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          contextMenuName,
          featureGeoJSON,
          attributeType,
          attributeConvert as IAttributeConvert
        );

        featureInfoList.push(selectedFeatureInfo);
      });
    }

    return featureInfoList;
  }

  /**
   * 右クリック処理
   * @param _map 地図
   * @param _latitude クリック座標緯度
   * @param _longitude クリック座標経度
   * @param _x クリック位置X
   * @param _y クリック位置Y
   * @param _width 地図幅
   * @param _height 地図高さ
   * @param _bBox 地図範囲（BBOX)
   */
  async getRightClickInfo(
    _map: Map,
    _latitude: number,
    _longitude: number,
    _x: number,
    _y: number,
    _width: number,
    _height: number,
    _bBox: number[]
  ): Promise<MapContextMenuItem[]> {
    if (
      OD_PREF_LAYER_NAME.includes(this._visibleODLayer.getLayerName()) &&
      (_map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
        _map.getView().getZoom() <=
          LAYER_MIN_ZOOMLEVEL[this._visibleODLayer.odDataType])
    ) {
      return null;
    } else if (
      !OD_PREF_LAYER_NAME.includes(this._visibleODLayer.getLayerName()) &&
      (_map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
        _map.getView().getZoom() <=
          LAYER_MIN_ZOOMLEVEL[this._visibleODLayer.odDataType])
    ) {
      return null;
    }

    //OD発選択レイヤから選択地点の地物を取得する
    const pixel = [_x, _y];
    const vectorFeatures = _map.getFeaturesAtPixel(pixel, {
      layerFilter: (layer: Layer) => {
        if (this._viewLayer == layer) {
          return true;
        }
      },
    });
    if (vectorFeatures.length !== 0) {
      return [
        new MapContextMenuItem(
          "OD交通量データ表示解除",
          this.onUnSelectedStartNode,
          true,
          null,
          null,
          "contextMenuItemOdRight"
        ),
      ];
    }

    let basecode = "";
    let startFeature = null;
    let attributeConverter = null;
    const cqlParamters = [
      new CqlParameter(
        this._visibleODLayer.getLayerName(),
        this._visibleODLayer.getCqlFilterString()
      ),
    ];
    const features = await FUNCTION_getSelectedFeatures(
      _bBox,
      cqlParamters,
      _x,
      _y,
      _height,
      _width
    );
    if (features.length !== 0) {
      const mapContextMenuItems: MapContextMenuItem[] = await Promise.all(
        features.map(async (feature) => {
          const id = feature.getId().toString();
          const layerKey = id.split(".")[0];

          const WFSGetter = new WFSGetFeatureManager();
          const wfsGeoJson = await WFSGetter.getInfo(
            URL_API,
            cqlParamters,
            null,
            feature.getId()
          ).catch((error) => {
            throw error;
          });
          const wfsFeature = new GeoJSON()
            .readFeatures(wfsGeoJson)
            .find((feature) => feature.getId() === id);
          if (!wfsFeature) return;
          /**
           * 地物の交通量に対する直径を求めるための条件
           * 交通量が[idx][0]以上なら、直径は[idx][1]
           */
          const TRAFFIC_TO_DIAMETER_STEPS = [
              [0, 10],
              [200000, 20],
              [400000, 30],
              [600000, 40],
              [800000, 50],
              [1000000, 60],
              [1200000, 70],
              [1400000, 80],
              [1600000, 90],
              [1800000, 100],
            ]
          //上りトリップ数合計を取得
          const inAndOutTripTotal =
            feature.get("上りトリップ数合計値");
          // 交通量がどの条件値より小さいか
          let index = TRAFFIC_TO_DIAMETER_STEPS.findIndex((arr) => inAndOutTripTotal < arr[0]);
            // どの条件値よりも大きかった場合
          if (index == -1) index = TRAFFIC_TO_DIAMETER_STEPS.length;
          //取得した地物のゾーンコードを拠点コードとして設定
          basecode = feature.get("ゾーンコード");

          // selectFeatureを生成（地物情報のみ）
          const geometry = wfsFeature.getGeometry();
          startFeature = new Feature({
            geometry,
          });

          //idを設定
          startFeature.setId(id);
          //スタイルとして設定するポリゴンの色やポイントサイズを取得
          const sizeOrColorSource = TRAFFIC_TO_DIAMETER_STEPS[index - 1][1];
          const pointSizeAndPolygonColor = FUNCTION_getPointSizeAndPolygonColor(
            Math.floor(sizeOrColorSource)
          );

          //レイヤがBゾーンかそれ以外かで対象地物のスタイルを設定
          if (OD_BZONE_LAYER_NAME.includes(layerKey)) {
            const selectStyle = new Style({
              stroke: new Stroke({ width: 3, color: "hotpink" }),
              fill: new Fill({
                color: asArray(pointSizeAndPolygonColor.polygonColor)
                  .slice(0, 3)
                  .concat(0.5),
              }),
            });
            startFeature.set(LEFT_SELECTED_STLE, selectStyle);
            startFeature.setStyle(selectStyle);
          } else {
            const selectStyle = new Style({
              image: new Circle({
                stroke: new Stroke({ width: 3, color: "hotpink" }),
                radius: pointSizeAndPolygonColor.pointSize,
                fill: new Fill({
                  color: "rgba(255,0,0,0.6)",
                }),
              }),
            });
            const leftClickStyle = new Style({
              image: new Circle({
                stroke: new Stroke({ width: 3, color: "hotpink" }),
                radius: pointSizeAndPolygonColor.pointSize,
                fill: new Fill({
                  color: "rgba(255,0,0,0.6)",
                }),
              }),
            });
            departureNodeRadiusArray.push(pointSizeAndPolygonColor.pointSize);
            startFeature.set(LEFT_SELECTED_STLE, leftClickStyle);
            startFeature.setStyle(selectStyle);
          }
          attributeConverter = new ODNodeAttributeConvert(
            wfsFeature,
            this._visibleODLayer.odDataType,
            this._visibleODLayer.carType
          );

          const mapContextMenuItem = new MapContextMenuItem(
            "OD交通量データ表示",
            this.onSelectedStartNode(
              basecode,
              startFeature,
              attributeConverter
            ),
            false,
            null,
            null,
            "contextMenuItemOdRight"
          );
          return mapContextMenuItem;
        })
      );

      return mapContextMenuItems;
    }
  }

  /**
   * OD量データ表示コールバック関数
   * @param startNode 選択中発ノード
   * @param startFeature 発ノード地物
   * @param attributeConverter OD量コンバータ
   */
  onSelectedStartNode(
    startNode: string,
    startFeature: Feature,
    attributeConverter: ODNodeAttributeConvert
  ) {
    return () => {
      this.tmpStartNode = startNode;
      this.tmpStartFeature = startFeature;
      this.tmpSelectedAttributeConverter = attributeConverter;
      this._vectorSource.clear();
      this.startNode = null;
      this.selectedAttributeConverter = null;
      this._odDisplayEvent.onOdNodeUnSelected(false);

      this._departureLayer.setVisible(false);
      this._arrivalLayer.setOpacity(Number.MIN_VALUE);
      this._arrivalLayer.setVisible(true);
      this._vectorSource.addFeature(startFeature);
      this._viewLayer.setVisible(true);
      this.isUpdateParams = true;

      //着ノードレイヤの表示変更
      this._arrivalWmsImageSource.updateParams({
        LAYERS: this._visibleODLayer.getLayerName(startNode),
        CQL_FILTER: this._visibleODLayer.getCqlFilterString(
          startNode,
          "onSelectedStartNode"
        ),
      });
    };
  }

  /**
   * OD量データ表示解除コールバック関数
   */
  onUnSelectedStartNode = () => {
    this.startNode = null;
    this.selectedAttributeConverter = null;
    this._vectorSource.clear();
    this._viewLayer.setVisible(false);
    this._arrivalLayer.setVisible(false);
    this._departureLayer.setVisible(true);
    this._odDisplayEvent.onOdNodeUnSelected(true);
  };

  /**
   * レイヤの表示状態を設定
   * @param isVisible 表示有無
   * @param odQuantityLayerName 対象OD量レイヤリスト
   */
  setVisible(isVisible: boolean, odQuantityLayerName: ODQuantityLayerName) {
    this._departureLayer.setVisible(false);
    this._arrivalLayer.setVisible(false);
    this._viewLayer.setVisible(false);
    if (isVisible) {
      this._visibleODLayer = odQuantityLayerName;
      this._departureWmsImageSource.updateParams({
        LAYERS: this._visibleODLayer.getLayerName(),
        CQL_FILTER: this._visibleODLayer.getCqlFilterString(),
      });
      this._departureLayer.setMinZoom(
        LAYER_MIN_ZOOMLEVEL[this._visibleODLayer.odDataType]
      );
      this._arrivalLayer.setMinZoom(
        LAYER_MIN_ZOOMLEVEL[this._visibleODLayer.odDataType]
      );
      this._viewLayer.setMinZoom(
        LAYER_MIN_ZOOMLEVEL[this._visibleODLayer.odDataType]
      );
      this._departureWmsImageSource.refresh();
      this._arrivalWmsImageSource.refresh();
      this._vectorSource.refresh();
      this._departureLayer.setVisible(true);
    } else {
      this._departureLayer.setVisible(false);
    }
  }

  /**
   * 表示中のレイヤ数を取得
   */
  getVisibleLayerCount() {
    if (
      this._departureLayer?.getVisible() ||
      this._arrivalLayer?.getVisible() ||
      this._viewLayer?.getVisible()
    ) {
      return 1;
    }
    return 0;
  }

  /**
   * OD量データ表示開始・終了時イベントを設定
   * @param odDisplayEvent 地点選択コールバック関数
   */
  setOdDisplayEvent(odDisplayEvent: IOdDisplayEvent): void {
    this._odDisplayEvent = odDisplayEvent;
  }
  /**
   * 表示レイヤが存在するか判定
   */
  isVisibleLayer(_map: Map) {
    return (
      LayerUtil.isVisibleLayer(_map, this._viewLayer) ||
      LayerUtil.isVisibleLayer(_map, this._arrivalLayer) ||
      LayerUtil.isVisibleLayer(_map, this._departureLayer)
    );
  }
}
